import React from 'react';
import AdminLayout from 'cms/core/shared/AdminLayout';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import SecurityIcon from '@mui/icons-material/Security';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import CategoryIcon from '@mui/icons-material/Category';
import ScheduleIcon from '@mui/icons-material/Schedule';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import PublicIcon from '@mui/icons-material/Public';

const settingsItems = [
  {
    key: 'translations',
    title: 'Translations',
    description: 'Manage multilingual keys and values.',
    icon: <TranslateIcon fontSize="medium" />,
    link: '/cms/translations',
    color: 'primary'
  },
  {
    key: 'jobtypes',
    title: 'Job Types',
    description: 'Manage job type categories and classifications.',
    icon: <WorkOutlineIcon fontSize="medium" />,
    link: '/cms/jobtypes',
    color: 'success'
  },
  {
    key: 'categories',
    title: 'Categories',
    description: 'Manage job categories with images.',
    icon: <CategoryIcon fontSize="medium" />,
    link: '/cms/categories',
    color: 'warning'
  },
  {
    key: 'schedules',
    title: 'Schedules',
    description: 'Manage work schedule types and classifications.',
    icon: <ScheduleIcon fontSize="medium" />,
    link: '/cms/schedules',
    color: 'info'
  },
  {
    key: 'benefits',
    title: 'Benefits',
    description: 'Manage employee benefits and perks.',
    icon: <CardGiftcardIcon fontSize="medium" />,
    link: '/cms/benefits',
    color: 'secondary'
  },
  {
    key: 'countries',
    title: 'Countries & Cities',
    description: 'Manage countries and their cities with world map.',
    icon: <PublicIcon fontSize="medium" />,
    link: '/cms/countries',
    color: 'primary'
  },
  {
    key: 'preferences',
    title: 'Preferences',
    description: 'Update global CMS defaults.',
    icon: <SettingsApplicationsIcon fontSize="medium" />,
    link: '#',
    color: 'secondary',
    disabled: true
  },
  {
    key: 'security',
    title: 'Security',
    description: 'Configure access and permissions.',
    icon: <SecurityIcon fontSize="medium" />,
    link: '#',
    color: 'info',
    disabled: true
  }
];

const SettingsPage = () => {
  return (
    <AdminLayout pageTitle="Settings">
      <Box>
        <Typography variant="h4" fontWeight={700} mb={1} color="text.primary">
          Workspace Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" mb={4}>
          Configure your CMS from a single place. Hover items to explore.
        </Typography>

        <Grid container spacing={2}>
          {settingsItems.map((item) => {
            const card = (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  height: '100%',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 4
                  },
                  opacity: item.disabled ? 0.6 : 1
                }}
              >
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: `${item.color}.light`,
                      color: `${item.color}.dark`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {item.icon}
                  </Box>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight={600} 
                    fontSize="0.95rem"
                    sx={{
                      color: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'text.primary'
                    }}
                  >
                    {item.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                    {item.description}
                  </Typography>
                  {!item.disabled && (
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: 'primary.main',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        '&:hover': {
                          color: 'primary.light'
                        }
                      }}
                    >
                      Manage →
                    </Typography>
                  )}
                </Stack>
              </Paper>
            );

            return (
              <Grid item xs={12} sm={6} md={4} lg={3} key={item.key}>
                {item.disabled ? (
                  card
                ) : (
                  <Box 
                    component={RouterLink} 
                    to={item.link} 
                    sx={{ 
                      textDecoration: 'none',
                      color: 'inherit',
                      '&:hover': {
                        '& .MuiTypography-subtitle1': {
                          color: 'primary.light'
                        }
                      }
                    }}
                  >
                    {card}
                  </Box>
                )}
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </AdminLayout>
  );
};

export default SettingsPage;

